import { useEffect, useRef, useState } from 'react';
import { databaseService } from '../services/database';
import { createPCAgentClient, PCAgentClient } from '../services/pcAgent';
import { fetchVersionsForComponent } from '../services/githubReleases';
import { fetchShellVersions } from '../services/shellReleases';
import { fetchVersionIndex } from '../services/operatorUpdate';
import { getThisDevice } from '../services/operatorDevice';
import {
  Room,
  PCInstance,
  RoomSoftwareTarget,
  PCSoftwareStatus,
  SoftwareComponent,
  RoomStreamingSettings,
  PCStreamingSettings,
  StreamingSettings,
  ConfigurationRelease,
  ExperienceLaunchArgs,
  LaunchArgsScope,
  LaunchArgsValues,
  LaunchArgsManifest,
  AgentConfig,
  AgentConfigValues,
  OperatorDevice,
} from '../types/database';
import StreamingSettingsForm, { summarizeSettings } from '../components/software/StreamingSettingsForm';
import LaunchArgsForm, { summarizeLaunchArgs } from '../components/software/LaunchArgsForm';
import AgentConfigForm, { summarizeAgentConfig, countAgentConfig } from '../components/software/AgentConfigForm';
import DeviceLabelEditor from '../components/software/DeviceLabelEditor';
import HeadsetsPanel from '../components/software/HeadsetsPanel';
import '../styles/software-view.css';

const COMPONENTS: { key: SoftwareComponent; label: string }[] = [
  { key: 'agent', label: 'Agent' },
  { key: 'ngr-convai', label: 'NGRConvai' },
  { key: 'hmd_shell', label: 'Headset shell' },
];

// Top-level tabs. Each is a self-contained concern; the page holds the shared
// room/PC fetch + PCAgentClient map, the tabs render slices of it.
type Tab = 'versions' | 'streaming' | 'launchargs' | 'config' | 'operators' | 'headsets';
const TABS: { key: Tab; label: string }[] = [
  { key: 'versions', label: 'Versions' },
  { key: 'streaming', label: 'Streaming' },
  { key: 'launchargs', label: 'Launch args' },
  { key: 'config', label: 'Config' },
  { key: 'operators', label: 'Operator devices' },
  { key: 'headsets', label: 'Headsets' },
];

// A config editor targets exactly one scope: the global row, a room row, or a PC row.
type ConfigEditor =
  | { scope: 'global' }
  | { scope: 'room'; roomId: string }
  | { scope: 'pc'; pc: PCInstance; roomId: string };

const normVer = (v?: string | null) => (v || '').replace(/^v/, '');
const versionsEqual = (a?: string | null, b?: string | null) =>
  normVer(a) !== '' && normVer(a) === normVer(b);

// pc_software_status is refreshed by each agent's heartbeat ~every 30s.
const isFresh = (lastSeen?: string) => {
  if (!lastSeen) return false;
  return Date.now() - new Date(lastSeen).getTime() < 90_000;
};

// Coarse relative time for device last-seen (they heartbeat on launch/resume,
// not on a fixed interval, so exact freshness isn't meaningful).
const timeAgo = (iso?: string): string => {
  if (!iso) return 'never';
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 90) return 'just now';
  const mins = secs / 60;
  if (mins < 60) return `${Math.round(mins)}m ago`;
  const hrs = mins / 60;
  if (hrs < 24) return `${Math.round(hrs)}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

// Shallow-merge partial config objects by section then key (later wins). Used to
// compute the effective inherited values shown as hints in a PC/room editor.
const mergeConfigs = (...cfgs: (AgentConfigValues | undefined)[]): AgentConfigValues => {
  const out: AgentConfigValues = {};
  for (const c of cfgs) {
    if (!c) continue;
    for (const [sec, keys] of Object.entries(c)) {
      out[sec] = { ...(out[sec] || {}), ...keys };
    }
  }
  return out;
};

export default function SoftwareView({ roomId }: { roomId?: string }) {
  const [activeTab, setActiveTab] = useState<Tab>('versions');
  const [rooms, setRooms] = useState<Room[]>([]);
  const [pcsByRoom, setPcsByRoom] = useState<Map<string, PCInstance[]>>(new Map());
  const [targets, setTargets] = useState<RoomSoftwareTarget[]>([]);
  const [statuses, setStatuses] = useState<PCSoftwareStatus[]>([]);
  const [versions, setVersions] = useState<Record<SoftwareComponent, string[]>>({
    agent: [],
    'ngr-convai': [],
    hmd_shell: [],
  });
  const [roomStreaming, setRoomStreaming] = useState<RoomStreamingSettings[]>([]);
  const [pcStreaming, setPcStreaming] = useState<PCStreamingSettings[]>([]);
  // One Business Streaming editor open at a time: room-level, or a PC override.
  const [streamEditor, setStreamEditor] = useState<{ roomId: string; pc?: PCInstance } | null>(null);
  // ---- Experience launch args ----
  const [launchArgs, setLaunchArgs] = useState<ExperienceLaunchArgs[]>([]);
  // Per room: the configuration_release rows of its configuration (all phases)
  // — source of the room's experiences and their pinned releases.
  const [roomReleases, setRoomReleases] = useState<Map<string, ConfigurationRelease[]>>(new Map());
  // Per room: which experience's launch args are expanded.
  const [argsExp, setArgsExp] = useState<Map<string, string>>(new Map());
  // One launch-args editor open at a time.
  const [argsEditor, setArgsEditor] = useState<{ roomId: string; scope: LaunchArgsScope; title: string; version?: string } | null>(null);
  // LaunchArgs.json manifests by `${experienceId}:${version || ''}` (null = fetched, none available).
  const [manifests, setManifests] = useState<Map<string, LaunchArgsManifest | null>>(new Map());
  const [manifestLoading, setManifestLoading] = useState(false);
  // ---- Agent config overrides ----
  const [agentConfig, setAgentConfig] = useState<AgentConfig[]>([]);
  const [configEditor, setConfigEditor] = useState<ConfigEditor | null>(null);
  // ---- Operator device version targeting ----
  const [operatorDevices, setOperatorDevices] = useState<OperatorDevice[]>([]);
  // Published Operator versions (from operator-android/index.json), newest first.
  const [operatorVersions, setOperatorVersions] = useState<string[]>([]);
  // Identity + running version of the device rendering this page.
  const [thisDevice, setThisDevice] = useState<{ deviceId: string; version: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Set<string>>(new Set());

  // One PCAgentClient per PC, reused across renders.
  const clients = useRef<Map<string, PCAgentClient>>(new Map());
  const clientFor = (pc: PCInstance): PCAgentClient => {
    let c = clients.current.get(pc.id);
    if (!c) {
      c = createPCAgentClient([pc.ip_address, pc.tailscale_ip], 9090, `pc_${pc.id}`);
      clients.current.set(pc.id, c);
    }
    return c;
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      // Follow the header room selector: show just the selected room, or all
      // rooms as a fallback when none is selected.
      let roomsList: Room[];
      if (roomId) {
        const room = await databaseService.getRoom(roomId);
        roomsList = room ? [room] : [];
      } else {
        roomsList = await databaseService.listRooms();
      }
      const pcLists = await Promise.all(roomsList.map((r) => databaseService.getPCInstancesInRoom(r.id)));
      const byRoom = new Map<string, PCInstance[]>();
      roomsList.forEach((r, i) => byRoom.set(r.id, pcLists[i]));
      const allPcIds = pcLists.flat().map((p) => p.id);

      const [targetRows, statusRows, roomStreamingRows, pcStreamingRows, launchArgRows, agentConfigRows, operatorDeviceRows, releaseLists] =
        await Promise.all([
          databaseService.listRoomSoftwareTargets(),
          databaseService.getPCSoftwareStatusByPCIds(allPcIds),
          databaseService.listRoomStreamingSettings(),
          databaseService.getPCStreamingSettingsByPCIds(allPcIds),
          databaseService.listExperienceLaunchArgs(),
          databaseService.listAgentConfig(),
          // Operator devices are fleet-wide (a device may have no room), so this
          // is not filtered by the room selector.
          databaseService.listOperatorDevices(),
          Promise.all(
            roomsList.map((r) =>
              r.configuration_id
                ? databaseService.getConfigurationReleases(r.configuration_id, 'production', true)
                : Promise.resolve([] as ConfigurationRelease[])
            )
          ),
        ]);
      const releasesByRoom = new Map<string, ConfigurationRelease[]>();
      roomsList.forEach((r, i) => releasesByRoom.set(r.id, releaseLists[i]));

      setRooms(roomsList);
      setPcsByRoom(byRoom);
      setTargets(targetRows);
      setStatuses(statusRows);
      setRoomStreaming(roomStreamingRows);
      setPcStreaming(pcStreamingRows);
      setLaunchArgs(launchArgRows);
      setAgentConfig(agentConfigRows);
      setOperatorDevices(operatorDeviceRows);
      setRoomReleases(releasesByRoom);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  // Reload fleet data whenever the selected room changes.
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // Version lists are best-effort (GitHub) — load once, independent of the fleet data.
  useEffect(() => {
    (async () => {
      const [agentV, convaiV, shellV] = await Promise.all([
        fetchVersionsForComponent('agent'),
        fetchVersionsForComponent('ngr-convai'),
        fetchShellVersions(),
      ]);
      setVersions({ agent: agentV, 'ngr-convai': convaiV, hmd_shell: shellV });
    })();
  }, []);

  // Published Operator versions (the only ones a device can be pinned to, since
  // each needs a published OTA bundle). Best-effort from operator-android/index.json.
  useEffect(() => {
    (async () => {
      const idx = await fetchVersionIndex();
      setOperatorVersions((idx?.versions ?? []).map((v) => v.version.replace(/^v/, '')));
    })();
  }, []);

  // Which device is this? Resolved once; the version comes from the running
  // bundle, so on Android it flips as soon as an OTA has been applied.
  useEffect(() => {
    (async () => {
      try {
        setThisDevice(await getThisDevice());
      } catch {
        // identity is a display nicety — never block the page on it
      }
    })();
  }, []);

  // All room names, keyed by id — the operators tab is fleet-wide and a device's
  // reported room may not be the one selected in the header.
  const [roomNames, setRoomNames] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    (async () => {
      const all = await databaseService.listRooms();
      setRoomNames(new Map(all.map((r) => [r.id, r.name])));
    })();
  }, []);

  const handleSetDeviceTarget = async (deviceId: string, raw: string) => {
    const value = raw === '' ? null : raw;
    const ok = await databaseService.setOperatorDeviceTarget(deviceId, value);
    if (!ok) {
      alert('Failed to save target version');
      return;
    }
    setOperatorDevices((prev) =>
      prev.map((d) => (d.device_id === deviceId ? { ...d, desired_version: value } : d))
    );
  };

  const handleSaveDeviceLabel = async (deviceId: string, label: string) => {
    const trimmed = label.trim() || null;
    const ok = await databaseService.setOperatorDeviceLabel(deviceId, trimmed);
    if (!ok) {
      alert('Failed to rename device');
      return;
    }
    setOperatorDevices((prev) =>
      prev.map((d) => (d.device_id === deviceId ? { ...d, label: trimmed } : d))
    );
  };

  const handleDeleteDevice = async (deviceId: string) => {
    if (!window.confirm('Remove this device from the registry? It will re-register if the app launches on it again.')) return;
    const ok = await databaseService.deleteOperatorDevice(deviceId);
    if (!ok) {
      alert('Failed to remove device');
      return;
    }
    setOperatorDevices((prev) => prev.filter((d) => d.device_id !== deviceId));
  };

  const targetFor = (roomId: string, component: SoftwareComponent): string | null => {
    const t = targets.find((x) => x.room_id === roomId && x.component === component);
    return t ? t.desired_version : null;
  };
  const statusFor = (pcId: string, component: SoftwareComponent): PCSoftwareStatus | undefined =>
    statuses.find((s) => s.pc_instance_id === pcId && s.component === component);

  const handleSetTarget = async (roomId: string, component: SoftwareComponent, raw: string) => {
    let value: string | null;
    if (raw === '__custom__') {
      const entered = window.prompt(`Enter ${component} version tag (e.g. v1.2.60):`, '');
      if (entered === null) return; // cancelled
      value = entered.trim() || null;
    } else {
      value = raw === '' ? null : raw;
    }
    const ok = await databaseService.upsertRoomSoftwareTarget(roomId, component, value);
    if (!ok) {
      alert('Failed to save desired version');
      return;
    }
    // Update local state in place.
    setTargets((prev) => {
      const next = prev.filter((t) => !(t.room_id === roomId && t.component === component));
      next.push({
        id: `${roomId}:${component}`,
        room_id: roomId,
        component,
        desired_version: value,
        updated_at: new Date().toISOString(),
      });
      return next;
    });
  };

  const runAction = async (key: string, fn: () => Promise<{ success: boolean; error?: string }>, confirmMsg?: string) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy((b) => new Set(b).add(key));
    try {
      const res = await fn();
      if (!res.success) {
        alert(`Failed: ${res.error || 'unknown error'}`);
      }
    } catch (e) {
      alert(`Failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy((b) => {
        const n = new Set(b);
        n.delete(key);
        return n;
      });
      // Reported status updates on the next heartbeat; refresh shortly after.
      setTimeout(load, 5000);
    }
  };

  const toggleExperimental = async (pc: PCInstance) => {
    const next = !pc.experimental;
    const ok = await databaseService.setPCExperimental(pc.id, next);
    if (!ok) {
      alert('Failed to update experimental flag');
      return;
    }
    setPcsByRoom((prev) => {
      const copy = new Map(prev);
      for (const [roomId, pcs] of copy) {
        copy.set(roomId, pcs.map((p) => (p.id === pc.id ? { ...p, experimental: next } : p)));
      }
      return copy;
    });
  };

  // ---- Business Streaming settings ----

  const roomStreamingFor = (roomId: string) => roomStreaming.find((r) => r.room_id === roomId);
  const pcStreamingFor = (pcId: string) => pcStreaming.find((p) => p.pc_instance_id === pcId);

  // Save (or clear, settings=null) the settings for the open editor's target.
  const handleSaveStreaming = async (settings: StreamingSettings | null) => {
    if (!streamEditor) return;
    const { roomId, pc } = streamEditor;
    const now = new Date().toISOString();

    if (pc) {
      const ok = settings
        ? await databaseService.upsertPCStreamingSettings(pc.id, settings)
        : await databaseService.deletePCStreamingSettings(pc.id);
      if (!ok) {
        alert('Failed to save Business Streaming override');
        return;
      }
      setPcStreaming((prev) => {
        const next = prev.filter((p) => p.pc_instance_id !== pc.id);
        if (settings) next.push({ id: pc.id, pc_instance_id: pc.id, settings, updated_at: now });
        return next;
      });
    } else {
      const ok = settings
        ? await databaseService.upsertRoomStreamingSettings(roomId, settings)
        : await databaseService.deleteRoomStreamingSettings(roomId);
      if (!ok) {
        alert('Failed to save Business Streaming settings');
        return;
      }
      setRoomStreaming((prev) => {
        const next = prev.filter((r) => r.room_id !== roomId);
        if (settings) next.push({ id: roomId, room_id: roomId, settings, updated_at: now });
        return next;
      });
    }
    setStreamEditor(null);
  };

  const applyStreamingConfirm = (label: string) =>
    `Apply Business Streaming settings on ${label} now? Business Streaming restarts where values change — an active headset stream is dropped.`;

  const applyStreamingToRoom = (roomId: string) => {
    // Only push to online PCs; offline PCs pick up the saved settings on next boot.
    const pcs = (pcsByRoom.get(roomId) || []).filter((pc) => databaseService.isPCOnline(pc.last_heartbeat));
    if (pcs.length === 0) {
      alert('No online PCs in this room. The saved settings apply on each PC’s next agent boot.');
      return;
    }
    if (!window.confirm(applyStreamingConfirm(`the ${pcs.length} online PC(s) in this room`))) return;
    pcs.forEach((pc) => runAction(`${pc.id}:streaming:apply`, () => clientFor(pc).applyStreamingSettings()));
  };

  // ---- Experience launch args ----

  const argsRowFor = (scope: LaunchArgsScope): ExperienceLaunchArgs | undefined =>
    launchArgs.find(
      (r) =>
        r.experience_id === scope.experienceId &&
        r.room_id === (scope.roomId ?? null) &&
        r.pc_instance_id === (scope.pcInstanceId ?? null) &&
        r.experience_release_id === (scope.experienceReleaseId ?? null)
    );

  // Experiences in the room's configuration (deduped across phases).
  const roomExperiencesFor = (roomId: string): { id: string; name: string }[] => {
    const seen = new Map<string, string>();
    for (const cr of roomReleases.get(roomId) || []) {
      if (cr.experience_id && !seen.has(cr.experience_id)) {
        seen.set(cr.experience_id, cr.experiences?.name || cr.experience_id);
      }
    }
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  };

  // Releases of an experience pinned by the room's configuration (any phase).
  const pinnedReleasesFor = (roomId: string, experienceId: string): { id: string; version: string; phases: string[] }[] => {
    const out = new Map<string, { version: string; phases: string[] }>();
    for (const cr of roomReleases.get(roomId) || []) {
      if (cr.experience_id !== experienceId || !cr.experience_release_id) continue;
      const entry = out.get(cr.experience_release_id) || {
        version: cr.experience_release?.version || '?',
        phases: [],
      };
      entry.phases.push(cr.phase);
      out.set(cr.experience_release_id, entry);
    }
    return [...out.entries()].map(([id, v]) => ({ id, ...v }));
  };

  // Fetch the LaunchArgs.json manifest by asking ALL of the room's PCs in
  // parallel, each capped at a few seconds — any online PC with the build
  // installed serves it, and an offline PC must not stall the editor (its
  // client would otherwise burn full LAN+Tailscale timeouts). null is cached
  // too ("no manifest reachable"), which drops the form to raw JSON editing.
  const loadManifest = async (roomId: string, experienceId: string, version?: string) => {
    const key = `${experienceId}:${version || ''}`;
    if (manifests.has(key)) return;
    setManifestLoading(true);
    try {
      const pcs = pcsByRoom.get(roomId) || [];
      const results = await Promise.all(
        pcs.map((pc) =>
          Promise.race([
            clientFor(pc).getLaunchArgsSchema(experienceId, version),
            new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000)),
          ])
        )
      );
      // A "Skip — edit raw JSON" click may have seeded null while we were
      // fetching; don't yank the open editor out from under the user.
      setManifests((prev) => (prev.has(key) ? prev : new Map(prev).set(key, results.find(Boolean) ?? null)));
    } finally {
      setManifestLoading(false);
    }
  };

  const openArgsEditor = (roomId: string, scope: LaunchArgsScope, title: string, version?: string) => {
    setArgsEditor({ roomId, scope, title, version });
    void loadManifest(roomId, scope.experienceId, version);
  };

  // Save (or clear, args=null) the row for the open editor's scope.
  const handleSaveArgs = async (args: LaunchArgsValues | null) => {
    if (!argsEditor) return;
    const { scope } = argsEditor;
    const ok = await databaseService.saveExperienceLaunchArgs(scope, args);
    if (!ok) {
      alert('Failed to save launch args');
      return;
    }
    setLaunchArgs((prev) => {
      const next = prev.filter(
        (r) =>
          !(
            r.experience_id === scope.experienceId &&
            r.room_id === (scope.roomId ?? null) &&
            r.pc_instance_id === (scope.pcInstanceId ?? null) &&
            r.experience_release_id === (scope.experienceReleaseId ?? null)
          )
      );
      if (args) {
        next.push({
          id: `${scope.experienceId}:${scope.roomId || ''}:${scope.pcInstanceId || ''}:${scope.experienceReleaseId || ''}`,
          experience_id: scope.experienceId,
          room_id: scope.roomId ?? null,
          pc_instance_id: scope.pcInstanceId ?? null,
          experience_release_id: scope.experienceReleaseId ?? null,
          args,
          updated_at: new Date().toISOString(),
        });
      }
      return next;
    });
    setArgsEditor(null);
  };

  // ---- Agent config overrides ----

  const globalConfigRow = (): AgentConfig | undefined =>
    agentConfig.find((r) => !r.room_id && !r.pc_instance_id);
  const roomConfigFor = (roomId: string): AgentConfig | undefined =>
    agentConfig.find((r) => r.room_id === roomId && !r.pc_instance_id);
  const pcConfigFor = (pcId: string): AgentConfig | undefined =>
    agentConfig.find((r) => r.pc_instance_id === pcId);

  // A PC is "config dirty" when an applicable agent_config row (global / its room
  // / itself) was edited after the PC last recorded a boot-applied timestamp —
  // i.e. a saved change is waiting for an agent restart.
  const isConfigDirty = (pc: PCInstance, roomId: string): boolean => {
    const rows = [globalConfigRow(), roomConfigFor(roomId), pcConfigFor(pc.id)].filter(Boolean) as AgentConfig[];
    if (rows.length === 0) return false;
    const newestMs = Math.max(...rows.map((r) => new Date(r.updated_at).getTime()));
    if (!pc.config_applied_at) return true; // overrides exist but the PC never reported applying any
    return newestMs > new Date(pc.config_applied_at).getTime();
  };

  const handleSaveConfig = async (config: AgentConfigValues | null) => {
    if (!configEditor) return;
    const scope =
      configEditor.scope === 'global'
        ? {}
        : configEditor.scope === 'room'
        ? { roomId: configEditor.roomId }
        : { pcInstanceId: configEditor.pc.id };

    const ok = await databaseService.saveAgentConfig(scope, config);
    if (!ok) {
      alert('Failed to save agent config');
      return;
    }

    const roomKey = 'roomId' in scope ? scope.roomId ?? null : null;
    const pcKey = 'pcInstanceId' in scope ? scope.pcInstanceId ?? null : null;
    setAgentConfig((prev) => {
      const next = prev.filter((r) => !((r.room_id ?? null) === roomKey && (r.pc_instance_id ?? null) === pcKey));
      if (config && Object.keys(config).length > 0) {
        next.push({
          id: `${roomKey || ''}:${pcKey || ''}`,
          room_id: roomKey,
          pc_instance_id: pcKey,
          config,
          updated_at: new Date().toISOString(),
        });
      }
      return next;
    });
    setConfigEditor(null);
  };

  // ---- Shared cell/select renderers ----

  const renderArgsScopeRow = (
    key: string,
    roomId: string,
    label: string,
    scope: LaunchArgsScope,
    inheritLabel: string,
    title: string,
    version?: string
  ) => {
    const row = argsRowFor(scope);
    return (
      <tr key={key}>
        <td className="software-pc-name">{label}</td>
        <td>
          {row ? (
            <span className="software-bs-override" title={JSON.stringify(row.args, null, 2)}>
              {summarizeLaunchArgs(row.args)}
            </span>
          ) : (
            <span className="software-bs-inherit">{inheritLabel}</span>
          )}
        </td>
        <td>
          <div className="software-actions">
            <button onClick={() => openArgsEditor(roomId, scope, title, version)}>{row ? 'Edit' : 'Set…'}</button>
          </div>
        </td>
      </tr>
    );
  };

  const renderStreamingCell = (pc: PCInstance, roomId: string) => {
    const override = pcStreamingFor(pc.id);
    const k = `${pc.id}:streaming:apply`;
    const online = databaseService.isPCOnline(pc.last_heartbeat);
    return (
      <td className="software-component-cell">
        <div className="software-version-line">
          {override ? (
            <span className="software-bs-override" title={JSON.stringify(override.settings, null, 2)}>
              override: {summarizeSettings(override.settings)}
            </span>
          ) : (
            <span className="software-bs-inherit">room</span>
          )}
        </div>
        <div className="software-actions">
          {/* Override… only writes the DB row — works whether the PC is on or off. */}
          <button onClick={() => setStreamEditor({ roomId, pc })}>Override…</button>
          {/* Apply pushes to the live agent, so it needs the PC on. When it's off
              the saved settings apply on the agent's next boot — no error. */}
          <button
            disabled={busy.has(k) || !online}
            title={online ? '' : 'PC offline — saved settings apply on the next agent boot'}
            onClick={() =>
              runAction(k, () => clientFor(pc).applyStreamingSettings(), applyStreamingConfirm(pc.pc_name || pc.id))
            }
          >
            Apply
          </button>
        </div>
      </td>
    );
  };

  const renderVersionSelect = (roomId: string, component: SoftwareComponent) => {
    const current = targetFor(roomId, component);
    const top = versions[component].slice(0, 10); // latest 10, newest first
    const known = new Set(top);
    return (
      <select
        className="software-version-select"
        value={current ?? ''}
        onChange={(e) => handleSetTarget(roomId, component, e.target.value)}
      >
        <option value="">Unmanaged</option>
        {/* preserve a pinned version older than the latest 10 so it stays selected */}
        {current && current !== '' && !known.has(current) && <option value={current}>{current}</option>}
        {top.length > 0 && <option value={top[0]}>Latest — {top[0]}</option>}
        {top.slice(1).map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
        <option value="__custom__">Custom…</option>
      </select>
    );
  };

  const renderComponentCell = (pc: PCInstance, roomId: string, component: SoftwareComponent) => {
    const st = statusFor(pc.id, component);
    const desired = targetFor(roomId, component);
    const installed = st?.current_version || (component === 'agent' ? pc.agent_version : undefined) || null;
    const drift = !!desired && !versionsEqual(desired, installed);
    const running = component === 'ngr-convai' ? !!st?.running : isFresh(st?.last_seen);
    const k = (action: string) => `${pc.id}:${component}:${action}`;
    const client = clientFor(pc);

    // The shell lives on the headsets, not the PC: no per-PC status row. The
    // room target is what matters here; installed versions are per headset
    // on the Headsets tab, and the agent pushes the target to every older
    // connected headset by itself.
    if (component === 'hmd_shell') {
      return (
        <td key={component} className="software-component-cell">
          <div className="software-version-line">
            <span className="software-installed" title="Installed shell versions are per headset: see the Headsets tab">
              {desired ? `→ ${desired}` : '—'}
            </span>
          </div>
        </td>
      );
    }

    return (
      <td key={component} className="software-component-cell">
        <div className="software-version-line">
          <span className={`software-dot ${running ? 'on' : 'off'}`} title={running ? 'running' : 'not running'} />
          <span className="software-installed">{installed || '—'}</span>
          {drift && <span className="software-drift" title={`Desired ${desired}`}>↯ {desired}</span>}
          {st?.last_seen && !isFresh(st.last_seen) && <span className="software-stale" title={`Last seen ${st.last_seen}`}>stale</span>}
        </div>
        <div className="software-actions">
          {component === 'agent' && (
            <>
              <button
                disabled={busy.has(k('update')) || !drift}
                onClick={() =>
                  runAction(
                    k('update'),
                    () => client.updateAgentVersion(desired as string),
                    `Update agent on ${pc.pc_name || pc.id} to ${desired}? The service will restart.`
                  )
                }
              >
                Update
              </button>
              <button
                disabled={busy.has(k('restart'))}
                onClick={() => runAction(k('restart'), () => client.restartAgent(), `Restart RealityAgent on ${pc.pc_name || pc.id}?`)}
              >
                Restart
              </button>
            </>
          )}
          {component === 'ngr-convai' && (
            <>
              <button
                disabled={busy.has(k('update')) || !drift}
                onClick={() =>
                  runAction(
                    k('update'),
                    () => client.updateConvai(desired as string),
                    `Update NGRConvai on ${pc.pc_name || pc.id} to ${desired}?`
                  )
                }
              >
                Update
              </button>
              <button disabled={busy.has(k('restart'))} onClick={() => runAction(k('restart'), () => client.restartConvai())}>
                Restart
              </button>
              <button disabled={busy.has(k('start'))} onClick={() => runAction(k('start'), () => client.startConvai())}>
                Start
              </button>
              <button
                disabled={busy.has(k('clear'))}
                onClick={() => {
                  const exp = window.prompt('Clear audio cache for which experience? Leave blank to clear ALL.', '');
                  if (exp === null) return;
                  runAction(k('clear'), () => client.clearConvaiCache(exp.trim() || undefined));
                }}
              >
                Clear cache
              </button>
            </>
          )}
        </div>
      </td>
    );
  };

  // Version select for a single Operator device. Only published versions are
  // offered (each needs a bundle to OTA to); "Unmanaged" clears the pin.
  const renderDeviceVersionSelect = (device: OperatorDevice) => {
    const current = device.desired_version ? normVer(device.desired_version) : '';
    const known = new Set(operatorVersions);
    return (
      <select
        className="software-version-select"
        value={current}
        onChange={(e) => handleSetDeviceTarget(device.device_id, e.target.value)}
      >
        <option value="">Unmanaged</option>
        {/* keep a pin that predates the currently-published list selectable */}
        {current && !known.has(current) && <option value={current}>{current}</option>}
        {operatorVersions.length > 0 && <option value={operatorVersions[0]}>Latest — {operatorVersions[0]}</option>}
        {operatorVersions.slice(1).map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </select>
    );
  };

  const renderOperatorDevices = () => {
    const thisDeviceBanner = thisDevice && (
      <p className="software-this-device">
        This device: <code>{thisDevice.deviceId.slice(0, 8)}</code> — running{' '}
        <strong>v{normVer(thisDevice.version)}</strong>
      </p>
    );
    if (operatorDevices.length === 0) {
      return (
        <>
          {thisDeviceBanner}
          <p className="software-empty">
            No Operator devices have registered yet. Launch RealityOperator on a device and it will appear here.
          </p>
        </>
      );
    }
    return (
      <>
      {thisDeviceBanner}
      <div className="software-table-scroll">
        <table className="software-table">
          <thead>
            <tr>
              <th>Device</th>
              <th>Platform</th>
              <th>Room</th>
              <th>Running</th>
              <th>Target version</th>
              <th>Last seen</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {operatorDevices.map((d) => {
              const drift = !!d.desired_version && !versionsEqual(d.desired_version, d.current_version);
              // Only Android reconciles OTA; note where a pin can't auto-apply.
              const otaCapable = d.platform === 'android';
              return (
                <tr key={d.device_id}>
                  <td className="software-pc-name">
                    <DeviceLabelEditor
                      label={d.label}
                      onSave={(v) => handleSaveDeviceLabel(d.device_id, v)}
                    />
                    <span className="software-pc-role" title={d.device_id}>
                      {d.device_id.slice(0, 8)}
                    </span>
                  </td>
                  <td>{d.platform}</td>
                  <td>{d.room_id ? roomNames.get(d.room_id) ?? '—' : <span className="software-bs-inherit">none</span>}</td>
                  <td>
                    v{normVer(d.current_version) || '?'}
                    {drift && (
                      <span className="software-stale" title={otaCapable ? 'Pending — applies on next launch/foreground' : 'Pinned version differs; this platform does not OTA-reconcile'}>
                        {' '}→ v{normVer(d.desired_version)} {otaCapable ? 'pending' : '(manual)'}
                      </span>
                    )}
                  </td>
                  <td>{renderDeviceVersionSelect(d)}</td>
                  <td title={d.last_seen}>{isFresh(d.last_seen) ? 'online' : timeAgo(d.last_seen)}</td>
                  <td>
                    <button onClick={() => handleDeleteDevice(d.device_id)} title="Remove from registry">
                      Remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </>
    );
  };

  // ---- Per-tab section renderers ----

  const renderVersionsForRoom = (room: Room, pcs: PCInstance[]) => (
    <section key={room.id} className="software-room-section">
      <div className="software-room-header">
        <span className="software-room-name">{room.name}</span>
        <div className="software-room-targets">
          {COMPONENTS.map((c) => (
            <label key={c.key} className="software-target-label">
              {c.label} desired:
              {renderVersionSelect(room.id, c.key)}
            </label>
          ))}
        </div>
      </div>
      {pcs.length === 0 ? (
        <p className="software-empty">No PCs in this room.</p>
      ) : (
        <div className="software-table-scroll">
          <table className="software-table">
            <thead>
              <tr>
                <th>PC</th>
                <th>Experimental</th>
                {COMPONENTS.map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pcs.map((pc) => (
                <tr key={pc.id} className={pc.experimental ? 'software-row-experimental' : ''}>
                  <td className="software-pc-name">
                    {pc.pc_name || pc.mac_address}
                    <span className="software-pc-role">{pc.role}</span>
                  </td>
                  <td>
                    <label className="software-exp-toggle">
                      <input type="checkbox" checked={!!pc.experimental} onChange={() => toggleExperimental(pc)} />
                      <span>{pc.experimental ? 'exempt' : ''}</span>
                    </label>
                  </td>
                  {COMPONENTS.map((c) => renderComponentCell(pc, room.id, c.key))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );

  const renderStreamingForRoom = (room: Room, pcs: PCInstance[]) => {
    const rs = roomStreamingFor(room.id);
    const onlineCount = pcs.filter((pc) => databaseService.isPCOnline(pc.last_heartbeat)).length;
    return (
      <section key={room.id} className="software-room-section">
        <div className="software-room-header">
          <span className="software-room-name">{room.name}</span>
        </div>
        <div className="software-bs-row">
          <span className="software-bs-label">Business Streaming:</span>
          {rs ? (
            <span className="software-bs-summary" title={JSON.stringify(rs.settings, null, 2)}>
              {summarizeSettings(rs.settings)}
            </span>
          ) : (
            <span className="software-bs-inherit">unmanaged</span>
          )}
          <div className="software-actions">
            <button onClick={() => setStreamEditor({ roomId: room.id })}>Edit</button>
            {rs && onlineCount > 0 && (
              <button onClick={() => applyStreamingToRoom(room.id)}>Apply to {onlineCount} online PC{onlineCount > 1 ? 's' : ''}</button>
            )}
          </div>
        </div>

        {streamEditor?.roomId === room.id && (
          <StreamingSettingsForm
            key={streamEditor.pc ? `pc-${streamEditor.pc.id}` : `room-${room.id}`}
            title={
              streamEditor.pc
                ? `Business Streaming override — ${streamEditor.pc.pc_name || streamEditor.pc.id} (on top of the room settings)`
                : `Business Streaming settings — ${room.name} (all PCs, applied on agent boot)`
            }
            initial={streamEditor.pc ? pcStreamingFor(streamEditor.pc.id)?.settings : rs?.settings}
            onSave={handleSaveStreaming}
            onClose={() => setStreamEditor(null)}
          />
        )}

        {pcs.length === 0 ? (
          <p className="software-empty">No PCs in this room.</p>
        ) : (
          <div className="software-table-scroll">
            <table className="software-table">
              <thead>
                <tr>
                  <th>PC</th>
                  <th>Streaming</th>
                </tr>
              </thead>
              <tbody>
                {pcs.map((pc) => (
                  <tr key={pc.id} className={pc.experimental ? 'software-row-experimental' : ''}>
                    <td className="software-pc-name">
                      {pc.pc_name || pc.mac_address}
                      <span className="software-pc-role">{pc.role}</span>
                    </td>
                    {renderStreamingCell(pc, room.id)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    );
  };

  const renderLaunchArgsForRoom = (room: Room, pcs: PCInstance[]) => {
    const roomExps = roomExperiencesFor(room.id);
    const selExpId = argsExp.get(room.id) || '';
    const selExp = roomExps.find((e) => e.id === selExpId);
    return (
      <section key={room.id} className="software-room-section">
        <div className="software-room-header">
          <span className="software-room-name">{room.name}</span>
        </div>
        <div className="software-bs-row">
          <span className="software-bs-label">Launch args:</span>
          {roomExps.length === 0 ? (
            <span className="software-bs-inherit">no experiences in this room's configuration</span>
          ) : (
            <select
              className="software-version-select"
              value={selExpId}
              onChange={(e) => {
                setArgsExp((prev) => new Map(prev).set(room.id, e.target.value));
                setArgsEditor(null);
              }}
            >
              <option value="">Select experience…</option>
              {roomExps.map((ex) => (
                <option key={ex.id} value={ex.id}>
                  {ex.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {argsEditor?.roomId === room.id &&
          (() => {
            const mKey = `${argsEditor.scope.experienceId}:${argsEditor.version || ''}`;
            if (!manifests.has(mKey) && manifestLoading) {
              return (
                <div className="software-bs-editor">
                  <div className="software-bs-editor-title">{argsEditor.title}</div>
                  <p className="software-bs-preview-empty">Fetching the LaunchArgs.json manifest from the room's PCs (a few seconds)…</p>
                  <div className="software-actions">
                    <button
                      onClick={() => setManifests((prev) => (prev.has(mKey) ? prev : new Map(prev).set(mKey, null)))}
                    >
                      Skip — edit raw JSON
                    </button>
                    <button onClick={() => setArgsEditor(null)}>Cancel</button>
                  </div>
                </div>
              );
            }
            return (
              <LaunchArgsForm
                key={`${mKey}:${argsEditor.title}`}
                title={argsEditor.title}
                manifest={manifests.get(mKey) ?? null}
                initial={argsRowFor(argsEditor.scope)?.args}
                onSave={handleSaveArgs}
                onClose={() => setArgsEditor(null)}
              />
            );
          })()}

        {selExp && (
          <div className="software-table-scroll">
            <table className="software-table">
              <thead>
                <tr>
                  <th>Launch-args scope (top overrides bottom-up: default → room → release → PC)</th>
                  <th>Configured args</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {renderArgsScopeRow(
                  `${room.id}:args:default`,
                  room.id,
                  `${selExp.name} default — ALL rooms`,
                  { experienceId: selExp.id },
                  'build defaults',
                  `Launch args — ${selExp.name} default (all rooms)`
                )}
                {renderArgsScopeRow(
                  `${room.id}:args:room`,
                  room.id,
                  `Room — ${room.name}`,
                  { experienceId: selExp.id, roomId: room.id },
                  'inherit',
                  `Launch args — ${selExp.name} in ${room.name}`
                )}
                {pinnedReleasesFor(room.id, selExp.id).map((rel) =>
                  renderArgsScopeRow(
                    `${room.id}:args:rel:${rel.id}`,
                    room.id,
                    `Release v${rel.version} (${rel.phases.join(', ')}) — ALL rooms`,
                    { experienceId: selExp.id, experienceReleaseId: rel.id },
                    'inherit',
                    `Launch args — ${selExp.name} v${rel.version} release override (all rooms on this release)`,
                    rel.version
                  )
                )}
                {pcs.map((pc) =>
                  renderArgsScopeRow(
                    `${room.id}:args:pc:${pc.id}`,
                    room.id,
                    `PC — ${pc.pc_name || pc.mac_address}`,
                    { experienceId: selExp.id, pcInstanceId: pc.id },
                    'inherit',
                    `Launch args — ${selExp.name} on ${pc.pc_name || pc.id} (PC override)`
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    );
  };

  const renderConfigForRoom = (room: Room, pcs: PCInstance[]) => {
    const roomCfg = roomConfigFor(room.id);
    const globalCfg = globalConfigRow();
    return (
      <section key={room.id} className="software-room-section">
        <div className="software-room-header">
          <span className="software-room-name">{room.name}</span>
        </div>
        <div className="software-bs-row">
          <span className="software-bs-label">Room config:</span>
          {roomCfg && countAgentConfig(roomCfg.config) > 0 ? (
            <span className="software-bs-override" title={JSON.stringify(roomCfg.config, null, 2)}>
              {summarizeAgentConfig(roomCfg.config)}
            </span>
          ) : (
            <span className="software-bs-inherit">inherits global / config.yaml</span>
          )}
          <div className="software-actions">
            <button onClick={() => setConfigEditor({ scope: 'room', roomId: room.id })}>{roomCfg ? 'Edit' : 'Set…'}</button>
          </div>
        </div>

        {configEditor?.scope === 'room' && configEditor.roomId === room.id && (
          <AgentConfigForm
            title={`Room config — ${room.name} (all PCs in this room)`}
            initial={roomCfg?.config}
            inherited={globalCfg?.config}
            onSave={handleSaveConfig}
            onClose={() => setConfigEditor(null)}
          />
        )}

        {pcs.length === 0 ? (
          <p className="software-empty">No PCs in this room.</p>
        ) : (
          <div className="software-table-scroll">
            <table className="software-table">
              <thead>
                <tr>
                  <th>PC</th>
                  <th>PC override</th>
                  <th>Applied</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pcs.map((pc) => {
                  const pcCfg = pcConfigFor(pc.id);
                  const dirty = isConfigDirty(pc, room.id);
                  const online = databaseService.isPCOnline(pc.last_heartbeat);
                  const rk = `${pc.id}:cfg:restart`;
                  return (
                    <tr key={pc.id}>
                      <td className="software-pc-name">
                        {pc.pc_name || pc.mac_address}
                        <span className="software-pc-role">{pc.role}</span>
                      </td>
                      <td>
                        {pcCfg && countAgentConfig(pcCfg.config) > 0 ? (
                          <span className="software-bs-override" title={JSON.stringify(pcCfg.config, null, 2)}>
                            {summarizeAgentConfig(pcCfg.config)}
                          </span>
                        ) : (
                          <span className="software-bs-inherit">inherits room</span>
                        )}
                      </td>
                      <td>
                        {!dirty ? (
                          <span className="software-bs-inherit">up to date</span>
                        ) : online ? (
                          <span className="software-drift" title="A saved config change has not been applied yet">
                            restart to apply
                          </span>
                        ) : (
                          // Offline PC: it will read the saved config on its next boot — nothing to do.
                          <span className="software-stale" title="Offline — the saved config applies on the next agent boot">
                            applies on next boot
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="software-actions">
                          {/* Editing writes the DB row — works on or offline. */}
                          <button onClick={() => setConfigEditor({ scope: 'pc', pc, roomId: room.id })}>
                            {pcCfg ? 'Edit' : 'Override…'}
                          </button>
                          {/* Restart only makes sense (and only works) on a live PC. */}
                          {dirty && online && (
                            <button
                              disabled={busy.has(rk)}
                              onClick={() =>
                                runAction(
                                  rk,
                                  () => clientFor(pc).restartAgent(),
                                  `Restart RealityAgent on ${pc.pc_name || pc.id} to apply the config change?`
                                )
                              }
                            >
                              Restart
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {configEditor?.scope === 'pc' && configEditor.roomId === room.id && (
          <AgentConfigForm
            title={`Config override — ${configEditor.pc.pc_name || configEditor.pc.id}`}
            initial={pcConfigFor(configEditor.pc.id)?.config}
            inherited={mergeConfigs(globalCfg?.config, roomCfg?.config)}
            onSave={handleSaveConfig}
            onClose={() => setConfigEditor(null)}
          />
        )}
      </section>
    );
  };

  if (loading)
    return (
      <div className="software-view">
        <p>Loading software status…</p>
      </div>
    );
  if (error)
    return (
      <div className="software-view">
        <p className="software-error">Error: {error}</p>
      </div>
    );

  const globalCfg = globalConfigRow();

  return (
    <div className="software-view">
      <div className="software-header">
        <h2>Software &amp; Config</h2>
        <button className="refresh-btn" onClick={load}>
          Refresh
        </button>
      </div>

      <div className="software-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={activeTab === t.key}
            className={`software-tab${activeTab === t.key ? ' active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="software-content">
        {activeTab === 'versions' && (
          <>
            <p className="software-hint">
              Set a room's desired version per component; non-experimental PCs reconcile toward it on boot. Installed
              versions/running state are reported by each agent's heartbeat (~30s). Use the per-PC buttons to update now.
            </p>
            {rooms.map((room) => renderVersionsForRoom(room, pcsByRoom.get(room.id) || []))}
          </>
        )}

        {activeTab === 'streaming' && (
          <>
            <p className="software-hint">
              The room's Business Streaming settings (plus any per-PC override) are enforced on agent boot, or immediately
              via Apply — Business Streaming restarts where values change. Experimental PCs only follow their own override.
            </p>
            {rooms.map((room) => renderStreamingForRoom(room, pcsByRoom.get(room.id) || []))}
          </>
        )}

        {activeTab === 'launchargs' && (
          <>
            <p className="software-hint">
              Launch args configure the CLI arguments an experience is started with (per experience, layered default →
              room → release → PC). The agent merges them on every launch, so changes take effect on the next launch — no
              apply needed.
            </p>
            {rooms.map((room) => renderLaunchArgsForRoom(room, pcsByRoom.get(room.id) || []))}
          </>
        )}

        {activeTab === 'config' && (
          <>
            <p className="software-hint">
              RealityAgent config overrides, layered global → room → PC (later wins) on top of each PC's config.yaml. The
              agent reads them at boot, so a saved change needs an agent restart to apply — a "restart to apply" badge
              appears on affected PCs. Secrets and identity are not editable here.
            </p>
            <section className="software-room-section">
              <div className="software-bs-row">
                <span className="software-bs-label">Global (all PCs):</span>
                {globalCfg && countAgentConfig(globalCfg.config) > 0 ? (
                  <span className="software-bs-override" title={JSON.stringify(globalCfg.config, null, 2)}>
                    {summarizeAgentConfig(globalCfg.config)}
                  </span>
                ) : (
                  <span className="software-bs-inherit">no global overrides (each PC uses its config.yaml)</span>
                )}
                <div className="software-actions">
                  <button onClick={() => setConfigEditor({ scope: 'global' })}>{globalCfg ? 'Edit' : 'Set…'}</button>
                </div>
              </div>
              {configEditor?.scope === 'global' && (
                <AgentConfigForm
                  title="Global config — every PC in the fleet"
                  initial={globalCfg?.config}
                  onSave={handleSaveConfig}
                  onClose={() => setConfigEditor(null)}
                />
              )}
            </section>
            {rooms.map((room) => renderConfigForRoom(room, pcsByRoom.get(room.id) || []))}
          </>
        )}

        {activeTab === 'operators' && (
          <>
            <h3 style={{ margin: '0 0 8px' }}>📱 Operator devices ({operatorDevices.length})</h3>
            <p className="software-hint">
              RealityOperator installs, targeted <strong>per device</strong> (device id is the anchor; room is reported
              metadata only). Pin a device to a published version and Android applies it as a silent OTA web-bundle swap on
              next launch/foreground. Electron installs self-update via their installer; web is display-only. Only published
              versions are selectable.
            </p>
            {renderOperatorDevices()}
          </>
        )}

        {activeTab === 'headsets' && (
          <HeadsetsPanel rooms={rooms} pcsByRoom={pcsByRoom} clientFor={clientFor} />
        )}
      </div>
    </div>
  );
}
